import { ChangeDetectorRef, Component, ErrorHandler, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core'
import { MatInput } from '@angular/material/input'
import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'

import { MatDialog } from '@angular/material/dialog'
import { MatSnackBar } from '@angular/material/snack-bar'
import { DateService } from '@lemon/core/service/date.service'
import { DebugService } from 'app/_angular/service/debug.service'
import { KirjanpitajaService } from 'app/_angular/service/kirjanpitaja/kirjanpitaja.service'
import { KirjautunutKayttajaService } from 'app/_angular/service/kirjautunut-kayttaja.service'
import { AreYouSureDialog, AreYouSureDialogData } from 'app/_jaettu-angular/_components/are-you-sure.dialog'
import { FileSaverService } from 'app/_jaettu-angular/service/file-saver'
import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'
import { Asiakas } from 'app/_jaettu-lemonator/model/asiakas'
import {
  ArkistoDownloadFileRequest,
  ArkistoDownloadFileResponse,
  ArkistoFile,
  ArkistoFileType,
  ArkistoGetDownloadLinkRequest,
  ArkistoGetDownloadLinkResponse,
  ArkistoService,
} from 'app/_jaettu-lemonator/service/arkisto.service'
import { Observable, Subject, combineLatest, distinctUntilChanged, firstValueFrom, map, of, switchMap, takeUntil } from 'rxjs'
import { AsiakasService } from '../../_angular/service/asiakas/asiakas.service'
import { ArkistoSisainenDialogData, ArkistoSisainenUploadDialog } from './arkisto-sisainen-upload.dialog'

interface ArkistoFileDisplay extends ArkistoFile {
  lataajaName: string
  ladattuDateString: string
  canPreview: boolean
  previewOpen: boolean
  previewLoading: boolean
  // previewFileByteArray: Promise<Uint8Array>
  previewFileDataUrl: Promise<string>
  previewType: PreviewType
}

type PreviewType = 'pdf' | 'jpg' | 'png'

@Component({
  standalone: false,
  templateUrl: './arkisto-sisainen.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./arkisto-sisainen.component.css'],
})
export class ArkistoSisainenComponent implements OnInit, OnDestroy {
  @ViewChild(MatInput, { static: true }) nimiInput: MatInput

  kirjanpitajaOnDevaajaObservable: Observable<boolean> = this._kirjautunutKayttajaService.kirjanpitajaOnDevaajaObservable
  urit: Observable<{ uri: string; display: string }>

  asiakasObservable: Observable<Asiakas>

  arkistoFilesObservable: Observable<ArkistoFileDisplay[]>

  private _ngUnsubscribe: Subject<void> = new Subject<void>()

  constructor(
    private _dialog: MatDialog,
    private _asiakasService: AsiakasService,
    private _firebase: FirebaseLemonator,
    private _arkistoService: ArkistoService,
    private _kirjanpitajaService: KirjanpitajaService,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
    private _dateService: DateService,
    private _snackbar: MatSnackBar,
    private _ladataanService: LadataanService,
    private _errorHandler: ErrorHandler,
    private _fileSaverService: FileSaverService,
    private _debugService: DebugService,
    private _changeDetectorRef: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.urit = this._asiakasService.nykyinenAsiakasAvainObservable.pipe(
      takeUntil(this._ngUnsubscribe),
      map(asiakas => {
        if (asiakas) {
          const uri = this._arkistoService.getArkistoMetadataCollection(asiakas.avain)
          return { uri: this._debugService.createFirestoreLink(uri), display: uri }
        }
        return null
      }),
    )

    this.asiakasObservable = this._asiakasService.nykyinenAsiakasObservable.pipe(
      takeUntil(this._ngUnsubscribe),
      distinctUntilChanged((a, b) => a?.avain === b?.avain),
    )

    this.arkistoFilesObservable = combineLatest([this.asiakasObservable, this._kirjanpitajaService.kirjanpitajienNimitiedotMapObservable]).pipe(
      switchMap(([asiakas, kirjanpitajienNimitiedotMap]) => {
        if (!asiakas) {
          of([])
        }

        return this._firebase
          .firestoreCollection<ArkistoFile>(this._arkistoService.getArkistoMetadataCollection(asiakas.avain))
          .where('poistettu', '==', false)
          .where('type', '==', ArkistoFileType.INTERNAL)
          .listen()
          .pipe(
            map(filesMetadata => {
              if (!filesMetadata?.length) {
                return []
              }

              return filesMetadata.map(file => {
                const kirjanpitaja = kirjanpitajienNimitiedotMap.get(file.luoja)
                const type = this._getPreviewType(file)
                const displayMetadata: ArkistoFileDisplay = {
                  ...file,
                  lataajaName: kirjanpitaja.etunimi + ' ' + kirjanpitaja.sukunimi,
                  ladattuDateString: this._dateService.muotoilePaiva(file.luotu, 'fi'),
                  canPreview: !!type,
                  previewOpen: false,
                  previewLoading: false,
                  previewFileDataUrl: null,
                  previewType: type,
                }
                return displayMetadata
              })
            }),
          )
      }),
    )
  }

  async openDialog() {
    const dialogData: ArkistoSisainenDialogData = {
      asiakas: await firstValueFrom(this.asiakasObservable),
      type: ArkistoFileType.INTERNAL,
    }
    this._dialog.open(ArkistoSisainenUploadDialog, { data: dialogData })
  }

  private _getPreviewType(file: ArkistoFile): PreviewType {
    const f = file.fileName.toLowerCase()
    if (f.endsWith('.pdf')) {
      return 'pdf'
    } else if (f.endsWith('.jpg') || f.endsWith('.jpeg')) {
      return 'jpg'
    } else if (f.endsWith('.png')) {
      return 'png'
    }
    return null
  }

  async stopFilePreview(file: ArkistoFileDisplay) {
    file.previewOpen = false
    file.previewLoading = false
  }

  async previewFile(file: ArkistoFileDisplay) {
    file.previewOpen = true
    file.previewLoading = true
    file.previewType = this._getPreviewType(file)
    const fileDownloadPromise: Promise<string> = this._downloadFileAsBase64EncodedString(file).then(str => {
      file.previewLoading = false
      this._changeDetectorRef.markForCheck()
      if (file.previewType === 'pdf') {
        return 'data:application/pdf;base64,' + str
      } else if (file.previewType === 'png') {
        return 'data:image/png;base64,' + str
      } else if (file.previewType === 'jpg') {
        return 'data:image/jpeg;base64,' + str
      }
      throw new Error('Unknown previewType: ' + file.previewType)
    })
    file.previewFileDataUrl = fileDownloadPromise
  }

  private async _downloadFileAsBase64EncodedString(file: ArkistoFile): Promise<string> {
    const asiakas = await firstValueFrom(this.asiakasObservable)

    const req: ArkistoDownloadFileRequest = {
      asiakasAvain: asiakas.avain,
      metadata: file,
    }

    const response = await this._firebase.functionsCall<ArkistoDownloadFileRequest, ArkistoDownloadFileResponse>('arkistoDownloadUploadedFile', req).then(resp => {
      if (!resp || resp.e) {
        throw new Error('Failed to fetch arkisto file ' + file.avain)
      }
      return resp
    })

    console.log(response.base64File.substring(0, 20))

    return response.base64File
  }

  async downloadFile(file: ArkistoFile) {
    this._ladataanService.aloitaLataaminen()

    try {
      const asiakas = await firstValueFrom(this.asiakasObservable)
      const req: ArkistoGetDownloadLinkRequest = {
        asiakasAvain: asiakas.avain,
        metadata: file,
      }

      const resp = await this._firebase.functionsCall<ArkistoGetDownloadLinkRequest, ArkistoGetDownloadLinkResponse>('arkistoGetDownloadLink', req)

      if (!resp || resp.e) {
        throw new Error('Failed to get download link! ' + (resp?.e || 'No response'))
      }
      if (resp.url) {
        // Laitetaan suoraan window.location.href, jotta tiedosto lähtee latautumaan myös Safarilla.
        // Latauslinkissä on content-disposition: attachment, joten tiedosto pitäisi aina latautua, ei aueta uudessa ikkunassa.
        window.location.href = resp.url
      }
    } catch (err) {
      this._errorHandler.handleError(err)
      this._snackbar.open('Tapahtui tuntematon virhe. Ole hyvä ja yritä uudelleen.', 'OK', { duration: 10 * 1000 })
    } finally {
      this._ladataanService.lopetaLataaminen()
    }
  }

  async markAsDeleted(metadata: ArkistoFile) {
    const areYouSureTexts: AreYouSureDialogData = {
      header: 'Poista tiedosto',
      rightAction: 'Poista',
      text: 'Oletko varma, että haluat poistaa arkistoitun tiedoston?',
    }
    const isSure = await firstValueFrom(this._dialog.open(AreYouSureDialog, { data: areYouSureTexts }).afterClosed())

    if (!isSure) {
      return
    }

    this._ladataanService.aloitaLataaminen()

    try {
      const uri = this._arkistoService.getArkistoMetadataUri(metadata.asiakasAvain, metadata.avain)
      const deleteData: Pick<ArkistoFile, 'poistettu'> = {
        poistettu: true,
      }
      await this._firebase.firestoreUpdateData(uri, deleteData)
    } catch (err) {
      this._errorHandler.handleError(err)
      this._snackbar.open('Tapahtui tuntematon virhe. Ole hyvä ja yritä uudelleen.', 'OK', { duration: 10 * 1000 })
    } finally {
      this._ladataanService.lopetaLataaminen()
    }
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }
}
